import { useEffect, useMemo, useState } from "react";
import { produce } from "immer";
import { useLocation } from "react-router";
import { parse, stringify } from "query-string";
import { Note } from "../../../../helbest";
import { API } from "../../../../request/api";
import { useLangNavigate } from "../../../../hooks/useLangNavigate";

const useFilter = (city_id, category_id, set_filter_open) => {
  const [filter_list, set_filter_list] = useState([]);

  const navigate = useLangNavigate();
  const location = useLocation();

  useEffect(() => {
    set_filter_list([]);
    API.get
      .productsFilters(category_id, city_id)
      .then((response) => {
        const initial_parsed_query_filters = parse(location.search);
        const parsed_query_filters = [];

        Object.keys(initial_parsed_query_filters).forEach((query_key) => {
          if (query_key !== "sort") {
            parsed_query_filters.push({
              parent_key: query_key,
              key: initial_parsed_query_filters[query_key],
            });
          }
        });

        const new_filter_list = produce(response.data, (draft) => {
          parsed_query_filters.forEach((query_item_object) => {
            draft.forEach((draft_item, draft_item_index) => {
              if (
                draft_item.key === query_item_object.parent_key &&
                draft_item.type === "collapse"
              ) {
                draft_item.list.forEach(
                  (draft_sub_item, draft_sub_item_index) => {
                    if (
                      query_item_object.key.includes(draft_sub_item.key) ||
                      query_item_object.key.includes(
                        parseInt(draft_sub_item.key)
                      )
                    ) {
                      draft[draft_item_index].list[
                        draft_sub_item_index
                      ].active = true;
                    } else {
                      draft[draft_item_index].list[
                        draft_sub_item_index
                      ].active = false;
                    }
                  }
                );
              }
            });
          });
        });

        set_filter_list(new_filter_list);
      })
      .catch((error) => {
        Note.error("Error while connecting to the server");
      });
    // eslint-disable-next-line
  }, [city_id, category_id]);

  const clear_filter = () => {
    const parsed_query = parse(location.search);
    const new_parsed_query = {};
    Object.keys(parsed_query).forEach((parsed_query_key) => {
      if (parsed_query_key === "sort") {
        new_parsed_query[parsed_query_key] = parsed_query[parsed_query_key];
      }
    });
    let new_url =
      location.pathname +
      (Object.keys(new_parsed_query).length > 0
        ? `?${stringify(new_parsed_query)}`
        : "");
    navigate(new_url);
    const new_filter_list = produce(filter_list, (draft) => {
      draft.forEach((draft_item, draft_index) => {
        if (draft_item.type === "collapse") {
          draft_item.list.forEach((draft_sub, draft_sub_index) => {
            if (draft_sub.active) {
              draft[draft_index].list[draft_sub_index].active = false;
            }
          });
        }
      });
    });
    set_filter_list(new_filter_list);
    set_filter_open(false);
  };

  const submit_filter = (new_filter_list = null) => {
    const submit_filter_list = [];
    (new_filter_list || filter_list).forEach((filter_item) => {
      if (filter_item.type === "collapse") {
        filter_item.list.forEach((sub_item) => {
          if (sub_item.active) {
            submit_filter_list.push({
              parent_key: filter_item.key,
              key: sub_item.key,
            });
          }
        });
      }
    });

    const parsed_query = parse(location.search);
    const new_parsed_query = {};

    Object.keys(parsed_query).forEach((parsed_query_key) => {
      if (parsed_query_key === "sort") {
        new_parsed_query[parsed_query_key] = parsed_query[parsed_query_key];
      }
    });

    submit_filter_list.forEach((filter_item) => {
      if (new_parsed_query[filter_item.parent_key]) {
        if (
          typeof new_parsed_query[filter_item.parent_key] === "string" ||
          typeof new_parsed_query[filter_item.parent_key] === "number"
        ) {
          new_parsed_query[filter_item.parent_key] = [
            new_parsed_query[filter_item.parent_key],
            filter_item.key,
          ];
        } else {
          new_parsed_query[filter_item.parent_key].push(filter_item.key);
        }
      } else {
        new_parsed_query[filter_item.parent_key] = filter_item.key;
      }
    });

    let new_url = `${location.pathname}${
      Object.keys(new_parsed_query).length > 0
        ? `?${stringify(new_parsed_query)}`
        : ""
    }`;

    navigate(new_url);
    set_filter_open(false);
  };

  const checkbox_change = (filter_item_index, sub_item_index, value) => {
    set_filter_list(
      produce((draft) => {
        draft[filter_item_index].list[sub_item_index].active = value;
      })
    );
  };

  const current_filters = useMemo(() => {
    const current_filters_keys = [];
    const parsed_query = parse(location.search);
    Object.keys(parsed_query).forEach((parsed_query_key) => {
      if (parsed_query_key !== "sort") {
        const new_filter_key = {
          parent_key: parsed_query_key,
          keys:
            typeof parsed_query[parsed_query_key] === "string"
              ? [parsed_query[parsed_query_key]]
              : parsed_query[parsed_query_key],
        };
        current_filters_keys.push(new_filter_key);
      }
    });

    const current_filter_objects = [];

    current_filters_keys.forEach((parent_item) => {
      parent_item.keys.forEach((key_item) => {
        filter_list.forEach((filter_item) => {
          if (filter_item.type === "collapse") {
            filter_item.list.forEach((sub_item) => {
              if (
                key_item === sub_item.key &&
                filter_item.key === parent_item.parent_key
              ) {
                current_filter_objects.push({
                  ...sub_item,
                  parent_key: parent_item.parent_key,
                });
              }
            });
          }
        });
      });
    });

    return current_filter_objects;
  }, [filter_list, location.search]);

  const delete_filter = (key, parent_key) => {
    const new_filter_list = produce(filter_list, (draft) => {
      draft.forEach((draft_item, draft_index) => {
        if (draft_item.type === "collapse") {
          draft_item.list.forEach((sub_draft_item, sub_draft_item_index) => {
            if (sub_draft_item.key === key && draft_item.key === parent_key) {
              draft[draft_index].list[sub_draft_item_index].active = false;
            }
          });
        }
      });
    });

    set_filter_list(new_filter_list);
    submit_filter(new_filter_list);
  };

  return [
    filter_list,
    set_filter_list,
    checkbox_change,
    clear_filter,
    submit_filter,
    current_filters,
    delete_filter,
  ];
};

export default useFilter;
